import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button } from "antd";
import { BellOutlined } from "@ant-design/icons";
import {
  getPartnerNotifications,
  getPartnerNotificationUnreadCount,
  markPartnerNotificationRead,
  markAllPartnerNotificationsRead,
} from "../services/partnerOrgService";
import NotificationPanel from "./NotificationPanel";

const POLL_MS = 30_000;

const PARTNER_TABS = [
  { key: "all",      label: "All",      filter: null },
  { key: "unread",   label: "Unread",   filter: n => !n.is_read },
  { key: "pickups",  label: "Pickups",  filter: n => n.notification_type === "NEW_PICKUP_REQUEST" },
  { key: "cancelled",label: "Cancelled",filter: n => n.notification_type === "PICKUP_CANCELLED" },
];

export default function PartnerNotificationBell({ onNavigate }) {
  const [unread,  setUnread]  = useState(0);
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(false);
  const pollRef = useRef();
  const openRef = useRef(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await getPartnerNotificationUnreadCount();
      setUnread(res.data?.unread_count ?? 0);
    } catch { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPartnerNotifications();
      setItems(Array.isArray(res.data) ? res.data : (res.data?.results ?? []));
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchUnread();
    pollRef.current = setInterval(() => {
      fetchUnread();
      if (openRef.current) fetchList();
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchUnread, fetchList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = () => {
    const next = !open;
    openRef.current = next;
    setOpen(next);
    if (next) fetchList();
  };

  const handleClose = () => {
    openRef.current = false;
    setOpen(false);
  };

  const handleItemClick = async (item) => {
    handleClose();
    if (!item.is_read) {
      try {
        await markPartnerNotificationRead(item.id);
        setItems(prev => prev.map(n => n.id === item.id ? { ...n, is_read: true } : n));
        setUnread(c => Math.max(0, c - 1));
      } catch { /* silent */ }
    }
    if (item.notification_type === "NEW_PICKUP_REQUEST") {
      onNavigate?.("queue");
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllPartnerNotificationsRead();
      setItems(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const badgeCount = unread > 99 ? "99+" : unread;

  return (
    <>
      <Badge count={badgeCount} size="small" offset={[-2, 4]} style={{ backgroundColor: "#722ed1" }}>
        <Button
          type="text"
          shape="circle"
          icon={<BellOutlined style={{ fontSize: 20 }} />}
          aria-label="Partner Notifications"
          onClick={handleOpen}
        />
      </Badge>

      <NotificationPanel
        visible={open}
        onClose={handleClose}
        notifications={items}
        unreadCount={unread}
        loading={loading}
        onMarkAllRead={handleMarkAll}
        onItemClick={handleItemClick}
        tabsConfig={PARTNER_TABS}
      />
    </>
  );
}
